import { players, type PlayerSeason } from "../data/players";
import { getPlayerProfile } from "../data/playerProfiles";
import { eraLabels, lineupSlots } from "./config";
import { getEffectivePositionTags, isSlotCompatible, sortSlotsByFit } from "./positions";
import type { DraftOffer, DraftPick, DraftPrompt, DraftRarity, GameMode, LineupSlot, SeasonResult } from "./types";
import { translations, type Language } from "./i18n";

const NOSTALGIA_MIN_PRE_2000_PICKS = 7;
const NOSTALGIA_MAX_2000_PICKS = 2;
const NOSTALGIA_MIN_SEASONS = 4;
const NOSTALGIA_MAX_SAME_CLUB_SEASON_PICKS = 4;
const NOSTALGIA_HARD_MIN_PRE_2000_PICKS = 8;
const NOSTALGIA_HARD_MAX_2000_PICKS = 1;
const NOSTALGIA_HARD_MIN_SEASONS = 5;
const NOSTALGIA_HARD_MAX_SAME_CLUB_SEASON_PICKS = 3;
const PROVINCIA_MIN_CULT_BAND_PICKS = 5;
const PROVINCIA_MAX_ICON_PICKS = 3;
const PROVINCIA_MIN_GRINDER_PICKS = 2;
const PROVINCIA_MAX_SAME_CLUB_SEASON_PICKS = 3;
const PROVINCIA_MEDIUM_MIN_CULT_BAND_PICKS = 4;
const PROVINCIA_MEDIUM_MAX_ICON_PICKS = 4;
const PROVINCIA_MEDIUM_MIN_GRINDER_PICKS = 1;
const PROVINCIA_MEDIUM_MAX_SAME_CLUB_SEASON_PICKS = 4;
const MODERN_DRAFT_MIN_CLUBS = 5;
const MODERN_DRAFT_MAX_SAME_CLUB_PICKS = 3;
const MODERN_DRAFT_MAX_SAME_CLUB_SEASON_PICKS = 2;
const DRAFT_PACK_SIZE = 6;
const PACK_MAX_SAME_CLUB = 2;
const PACK_MAX_SAME_CLUB_SEASON = 1;
const MODERN_HARD_MIN_CLUBS = 5;
const MODERN_HARD_MAX_HEADLINE_PICKS = 3;
const MODERN_HARD_MIN_BALANCE_PICKS = 4;
const MODERN_HARD_MAX_SAME_CLUB_PICKS = 3;
const MODERN_HARD_MAX_SAME_CLUB_SEASON_PICKS = 2;
const MODERN_DENSE_CLUB_MIN_WEIGHT = 0.22;
const MODERN_DENSE_CLUB_MAX_WEIGHT = 1.48;
const NOSTALGIA_DENSE_CLUB_MIN_WEIGHT = 0.5;
const NOSTALGIA_DENSE_CLUB_MAX_WEIGHT = 1.35;
const NOSTALGIA_DUPLICATE_NAME_MIN_WEIGHT = 0.78;
const ICON_RARITY_PEAK_THRESHOLD = 99;
const CHAMPION_RARITY_PEAK_THRESHOLD = 96;
const RARE_RARITY_PEAK_THRESHOLD = 92;
const ICON_LANE_MINIMUM_PEAK = 99;
const ICON_NON_LANE_EXPOSURE_PENALTY = 180;
const CHAMPION_NON_LANE_EXPOSURE_PENALTY = 110;
const RARE_NON_LANE_EXPOSURE_PENALTY = 32;
const MAX_90_PLUS_OFFERS_PER_PACK = 1;
const MAX_94_PLUS_OFFERS_PER_PACK = 1;
const ELITE_OFFER_SCORE_FLOOR = 90;
const HEADLINE_OFFER_SCORE_FLOOR = 94;
const ICON_OFFER_PEAK_FLOOR = 99;
const PROVINCIA_MIN_CLUBS = 3;
const MAX_ASSIGNMENT_BRANCHES_PER_SLOT = 8;
const PERFECT_SEASON_CHANCE_CAP = 0.012;
const ELITE_RATING_FLOOR = 99.15;
const SERIE_A_PRESSURE_TAX = 1.55;
const provinciaGrinderCache = new Map<string, boolean>();
const assignmentFeasibilityCache = new Map<string, boolean>();
let modernGlobalClubDensityCache: Record<string, number> | undefined;
let nostalgiaGlobalClubDensityCache: Record<string, number> | undefined;
let nostalgiaHistoricalNameCountsCache: Record<string, number> | undefined;

export function createPrompt(mode: GameMode, round: number, forcedClub?: GameMode["clubs"][number], lang: Language = "it", promptSeed = "default"): DraftPrompt {
  const promptClubs = forcedClub ? [forcedClub] : getPromptClubs(mode);
  const clubPool = promptClubs.length > 0 ? promptClubs : mode.clubs;
  const clubOffset = Math.floor(seededNumber(`${promptSeed}-${mode.id}-club`) * clubPool.length);
  const eraOffset = Math.floor(seededNumber(`${promptSeed}-${mode.id}-era`) * mode.eras.length);
  const club = clubPool[(round * 3 + mode.id.length + clubOffset) % clubPool.length];
  const era = mode.eras[(round * 2 + mode.name.length + eraOffset) % mode.eras.length];
  const eraLabel = translations[lang][era as keyof typeof translations["it"]] || eraLabels[era] || era;

  return {
    club,
    era,
    label: `${club} · ${eraLabel}`,
    detail: getPromptDetail(mode.id, club, era, lang),
  };
}

function getPromptClubs(mode: GameMode) {
  const eligibleClubs = new Set(getModePool(mode).map((player) => player.club));
  return mode.clubs.filter((club) => eligibleClubs.has(club));
}

function getPromptDetail(modeId: string, club: string, era: string, lang: Language) {
  const key = `${club}-${era}`;
  const italian: Record<string, string> = {
    "Lazio-anni2000": "Sera da Olimpico, maglia Cirio/Siemens e centrocampo con lanci lunghi che tagliano l'aria.",
    "Lazio-anni90": "Quando lo scudetto passava da Mancini in panchina, punizioni mancine e un Olimpico pieno di bandiere.",
    "Parma-anni90": "Gialloblu da coppa UEFA: portiere bambino prodigio, difensori duri e fantasisti sudamericani.",
    "Sampdoria-anni80": "Marassi di Boskov, Coppa Italia alzata e i gemelli del gol che stanno per prendersi la Serie A.",
    "Sampdoria-anni90": "Marassi pieno, maglia blucerchiata perfetta e telecronaca che aspetta Vialli-Mancini.",
    "Fiorentina-anni90": "Viola Nintendo, Batigol che calcia da fermo e il Franchi che sembra una cassa di risonanza.",
    "Milan-anni2000": "Notte europea, rombo a centrocampo e un dieci brasiliano che parte in conduzione.",
    "Inter-anni2010": "Triplete, pioggia di cori e una squadra che sa soffrire prima di colpire.",
    "Napoli-anni80": "Diego al San Paolo, maglie Ennerre e il Sud che cambia il peso del campionato.",
    "Roma-anni2000": "Trequartista, centravanti pesante e terzini che spingono come nello scudetto giallorosso.",
    "Juventus-anni2010": "Rimonta allegriana, BBC dietro e una Joya che accende il finale di stagione.",
    "Cagliari-anni70": "Amsicora, Rombo di Tuono e scudetto sull'isola: la provincia diventa centro del campionato.",
    "Torino-anni70": "Comunale pieno, Radice in panchina, Pulici-Graziani e un derby che pesa come lo scudetto.",
    "Juventus-anni70": "Trapattoni giovane, Zoff dietro, Causio largo e Bettega a staccare in un Comunale bianconero.",
    "Venezia-anni90": "Penzo, nebbia salmastra, Maniero a sportellare e Recoba che arriva a gennaio a cambiare la trama.",
    "Reggina-anni90": "Granillo pieno, prima Serie A amaranto, Pirlo in prestito e Kallon che attacca lo spazio.",
    "Lecce-anni90": "Via del Mare, Cavasin, Lucarelli che non perdona e Sesa che mette cross da videocassetta.",
    "Salernitana-anni90": "Arechi pieno, Delio Rossi, Ringhio ventenne e Di Vaio che prova a salvare tutto per un punto.",
    "Empoli-anni90": "Castellani compatto, Spalletti in panchina, Esposito che segna e Martusciello che cuce tutto.",
    "Foggia-anni90": "Zaccheria elettrico, Zemanlandia, linea altissima e tridente Baiano-Signori-Rambaudi.",
    "Perugia-anni90": "Curi acceso, Nakata che tira da lontano e provincia umbra che entra nel calcio globale.",
    "Vicenza-anni90": "Menti pieno, Guidolin in panchina e il profumo di Coppa delle Coppe ancora addosso.",
    "Piacenza-anni90": "Garilli, maglie tutte italiane, Vierchowod che guida la linea e punti strappati col coltello.",
    "Brescia-anni2000": "Rigamonti, Mazzone, Baggio che accarezza il pallone e Hubner che fuma l'area.",
    "Bari-anni2000": "San Nicola, Fascetti, Cassano diciassettenne e l'Inter che guarda il pallone passare.",
    "Verona-anni80": "Bentegodi da sorteggio arbitrale: Bagnoli, Elkjaer senza scarpa e Briegel che travolge la fascia.",
    "Chievo-anni2000": "Mussi volanti: Lupatelli col 10, Corini a disegnare e Delneri che pressa tutta la Serie A.",
    "Bologna-anni90": "Dall'Ara da videocassetta: Baggio rinasce, Andersson fa sponda e Ulivieri sorride sotto i baffi.",
    "Bologna-moderna": "Dall'Ara pieno di sciarpe, costruzione dal basso e difensori che sembrano registi.",
    "Atalanta-moderna": "Gasperini mode: uomo contro uomo, strappi continui e provincia che morde l'Europa.",
    "Palermo-anni2000": "Barbera di Zamparini: Miccoli che inventa, Pastore leggerissimo e Cavani ancora rosanero.",
    "Palermo-anni2010": "Barbera rosa-nero, trequartisti leggeri e numeri dieci pescati prima degli altri.",
    "Udinese-anni2010": "Friuli laboratorio: scouting globale, punte tecniche e portieri che parano rigori.",
  };
  const english: Record<string, string> = {
    "Lazio-anni2000": "Olimpico night, old shirt sponsors and long diagonal passes through midfield.",
    "Lazio-anni90": "A title-era Lazio mood: left-footed free kicks, flags, and a stacked Olimpico.",
    "Parma-anni90": "UEFA Cup Parma: young keepers, hard defenders, and South American flair.",
    "Sampdoria-anni80": "Boskov's Marassi, a Coppa Italia lift, and the goal twins getting ready to take over Serie A.",
    "Sampdoria-anni90": "Marassi packed, the perfect blue-ringed shirt, and Vialli-Mancini memories.",
    "Fiorentina-anni90": "Purple Nintendo years, Batigol thunder and the Franchi shaking.",
    "Milan-anni2000": "European night, diamond midfield, and a Brazilian ten carrying the ball.",
    "Inter-anni2010": "Treble mood: suffer first, then punish the match.",
    "Napoli-anni80": "Diego at the San Paolo, Ennerre shirts, and southern football changing the league.",
    "Roma-anni2000": "A free ten, a heavy striker, and full-backs flying like Roma's Scudetto side.",
    "Juventus-anni2010": "Allegri comeback years: BBC behind and the Joya lighting up the season.",
    "Cagliari-anni70": "Amsicora, Riva's thunder, and the island becoming the center of Italian football.",
    "Torino-anni70": "Packed Comunale, Radice on the bench, Pulici-Graziani and a derby heavy enough to decide the title.",
    "Juventus-anni70": "Young Trapattoni, Zoff behind, Causio wide and Bettega rising in a black-and-white Comunale.",
    "Venezia-anni90": "The Penzo, salty fog, Maniero battling center-backs and Recoba arriving in January to rewrite the story.",
    "Reggina-anni90": "A packed Granillo, Reggina's first Serie A season, Pirlo on loan and Kallon attacking space.",
    "Lecce-anni90": "Via del Mare, Cavasin, Lucarelli punishing chances and Sesa crossing like a VHS highlight.",
    "Salernitana-anni90": "The Arechi packed, Delio Rossi, young Ringhio and Di Vaio trying to save everything by one point.",
    "Empoli-anni90": "A tight Castellani, Spalletti on the bench, Esposito scoring and Martusciello stitching play together.",
    "Foggia-anni90": "An electric Zaccheria, Zemanlandia, a wild high line and the Baiano-Signori-Rambaudi front three.",
    "Perugia-anni90": "The Curi buzzing, Nakata shooting from range, and Umbrian province stepping into global football.",
    "Vicenza-anni90": "The Menti packed, Guidolin on the bench, and Cup Winners' Cup perfume still in the air.",
    "Piacenza-anni90": "Garilli, all-Italian shirts, Vierchowod leading the line, and points scraped out by hand.",
    "Brescia-anni2000": "Rigamonti, Mazzone, Baggio caressing the ball, and Hubner haunting the box.",
    "Bari-anni2000": "San Nicola, Fascetti, teenage Cassano, and Inter watching the ball glide past.",
    "Verona-anni80": "Bentegodi magic: Bagnoli, Elkjaer losing a boot, and Briegel storming through midfield.",
    "Chievo-anni2000": "Flying donkeys: Lupatelli wearing 10, Corini drawing passes, and Delneri pressing Serie A.",
    "Bologna-anni90": "Dall'Ara on videotape: Baggio reborn, Andersson as the target, and Ulivieri under the moustache.",
    "Palermo-anni2000": "Zamparini's Barbera: Miccoli creating, feather-light Pastore, and Cavani still in pink and black.",
  };
  const detailMaps: Partial<Record<Language, Record<string, string>>> = {
    it: italian,
    en: english,
  };
  const t = translations[lang] as any;
  const eraLabel = t[era] || eraLabels[era] || era;
  const fallback = t.promptFallback(club, eraLabel);

  return detailMaps[lang]?.[key] || fallback;
}

export function getCandidates(
  mode: GameMode,
  prompt: DraftPrompt,
  slot: LineupSlot,
  pickedIds: Set<string>,
  forcedClub?: GameMode["clubs"][number],
  lang: Language = "it",
  formationSlots: LineupSlot[] = lineupSlots,
): PlayerSeason[] {
  return getDraftOffers(mode, prompt, slot, [], pickedIds, forcedClub, lang, formationSlots).map((offer) => offer.player);
}

export function getDraftOffers(
  mode: GameMode,
  prompt: DraftPrompt,
  slot: LineupSlot,
  picks: DraftPick[],
  explicitPickedIds?: Set<string>,
  forcedClub?: GameMode["clubs"][number],
  lang: Language = "it",
  formationSlots: LineupSlot[] = lineupSlots,
  packSeed = "base",
): DraftOffer[] {
  const pickedIds = explicitPickedIds ?? new Set(picks.map((pick) => pick.player.id));
  const draftRound = Math.max(picks.length, pickedIds.size);
  const pickedNames = new Set([
    ...picks.map((pick) => normalizeName(pick.player.name)),
    ...players.filter((player) => pickedIds.has(player.id)).map((player) => normalizeName(player.name)),
  ]);
  const pool = getModePool(mode, forcedClub);
  const availableForSlot = pool.filter(
    (player) => !pickedIds.has(player.id) && !pickedNames.has(normalizeName(player.name)) && isSlotCompatible(player, slot),
  );
  const useFormationOrderFallback = explicitPickedIds !== undefined && picks.length === 0;
  const scarcitySafeAvailable = availableForSlot.filter((player) =>
    canCompleteFutureSlots(pool, pickedIds, pickedNames, player, slot, draftRound, formationSlots, mode, picks, useFormationOrderFallback),
  );
  const available = scarcitySafeAvailable.length > 0 ? scarcitySafeAvailable : availableForSlot;
  const lanes = getPackPlan(mode.id, draftRound, lang);
  const selected: DraftOffer[] = [];

  for (const lane of lanes) {
    const candidate = pickForLane(available, selected, lane, mode, prompt, picks, packSeed);
    if (candidate) {
      selected.push(toOffer(candidate, slot, lane.label, prompt, picks, lang));
    }
  }

  const fillerCandidates = shuffled(available, `${packSeed}-${mode.id}-${slot.id}-${draftRound}`).filter(
    (candidate) =>
      !selected.some((offer) => offer.player.id === candidate.id || samePlayer(offer.player, candidate)) &&
      isWithinPackEliteExposureBudget(candidate, selected) &&
      isWithinPackTeamExposureBudget(candidate, selected, mode.id),
  );
  const nonEliteFillers = fillerCandidates.filter((candidate) => candidate.score < ELITE_OFFER_SCORE_FLOOR && getIconPeak(candidate) < ICON_OFFER_PEAK_FLOOR);
  const eliteFillers = fillerCandidates.filter((candidate) => candidate.score >= ELITE_OFFER_SCORE_FLOOR || getIconPeak(candidate) >= ICON_OFFER_PEAK_FLOOR);

  for (const player of [...nonEliteFillers, ...eliteFillers]) {
    if (selected.some((offer) => offer.player.id === player.id || samePlayer(offer.player, player))) {
      continue;
    }

    selected.push(toOffer(player, slot, "Sorpresa", prompt, picks, lang));
    if (selected.length >= DRAFT_PACK_SIZE) {
      break;
    }
  }

  const relaxedFillers = shuffled(available, `${packSeed}-${mode.id}-${slot.id}-${draftRound}-relaxed`).filter(
    (candidate) =>
      !selected.some((offer) => offer.player.id === candidate.id || samePlayer(offer.player, candidate)),
  );
  const relaxedNonEliteFillers = relaxedFillers.filter(
    (candidate) => candidate.score < ELITE_OFFER_SCORE_FLOOR && getIconPeak(candidate) < ICON_OFFER_PEAK_FLOOR,
  );
  const relaxedEliteFillers = relaxedFillers.filter(
    (candidate) => candidate.score >= ELITE_OFFER_SCORE_FLOOR || getIconPeak(candidate) >= ICON_OFFER_PEAK_FLOOR,
  );

  for (const player of [...relaxedNonEliteFillers, ...relaxedEliteFillers]) {
    if (selected.some((offer) => offer.player.id === player.id || samePlayer(offer.player, player))) {
      continue;
    }

    selected.push(toOffer(player, slot, "Sorpresa", prompt, picks, lang));
    if (selected.length >= DRAFT_PACK_SIZE) {
      break;
    }
  }

  return selected.slice(0, DRAFT_PACK_SIZE);
}

export function getMixedDraftOffers(
  mode: GameMode,
  prompt: DraftPrompt,
  picks: DraftPick[],
  forcedClub?: GameMode["clubs"][number],
  lang: Language = "it",
  formationSlots: LineupSlot[] = lineupSlots,
  packSeed = "base",
): DraftOffer[] {
  const filledSlotIds = new Set(picks.map((pick) => pick.slot.id));
  const openSlots = formationSlots.filter((slot) => !filledSlotIds.has(slot.id));

  if (openSlots.length === 0) {
    return [];
  }

  const slotOrder = shuffledSlots(openSlots, `${packSeed}-${mode.id}-${prompt.label}-${picks.length}`);
  const offersBySlot = new Map(
    slotOrder.map((slot) => [
      slot.id,
      getDraftOffers(mode, prompt, slot, picks, undefined, forcedClub, lang, formationSlots, `${packSeed}-${slot.id}`),
    ]),
  );
  const selected: DraftOffer[] = [];
  const overflow: DraftOffer[] = [];
  const hardMixedCandidates: DraftOffer[] = [];

  for (let pass = 0; selected.length < DRAFT_PACK_SIZE && pass < DRAFT_PACK_SIZE; pass += 1) {
    for (const slot of slotOrder) {
      const offer = offersBySlot.get(slot.id)?.[pass];
      if (!offer || selected.some((item) => item.player.id === offer.player.id || samePlayer(item.player, offer.player))) {
        continue;
      }

      if (mode.id === "draft-hard") {
        hardMixedCandidates.push(offer);
        continue;
      }

      if (isWithinPackTeamExposureBudget(offer.player, selected, mode.id)) {
        selected.push(offer);
      } else if (!overflow.some((item) => item.player.id === offer.player.id || samePlayer(item.player, offer.player))) {
        overflow.push(offer);
      }

      if (selected.length >= DRAFT_PACK_SIZE) {
        break;
      }
    }
  }

  if (mode.id === "draft-hard") {
    selected.push(...chooseHardMixedOffers(hardMixedCandidates, `${packSeed}-${mode.id}-${prompt.label}-${picks.length}`));
  } else {
    if (selected.length === 0) {
      for (const offer of overflow) {
        if (!selected.some((item) => item.player.id === offer.player.id || samePlayer(item.player, offer.player))) {
          selected.push(offer);
          break;
        }
      }
    }

    for (const offer of overflow) {
      if (selected.length >= DRAFT_PACK_SIZE) {
        break;
      }

      if (!selected.some((item) => item.player.id === offer.player.id || samePlayer(item.player, offer.player))) {
        selected.push(offer);
      }
    }
  }

  return selected.map((offer) => {
    const slotOptions = sortSlotsByFit(offer.player, slotOrder.filter((slot) =>
      isSlotCompatible(offer.player, slot) ||
      offersBySlot.get(slot.id)?.some((candidate) => samePlayer(candidate.player, offer.player)),
    ));

    const bestSlot = sortSlotsByFit(offer.player, [offer.slot, ...slotOptions])[0] ?? offer.slot;

    return {
      ...offer,
      slot: bestSlot,
      slotOptions: slotOptions.length > 0 ? slotOptions : [bestSlot],
    };
  });
}

function chooseHardMixedOffers(candidates: DraftOffer[], seed: string) {
  const uniqueCandidates = candidates.filter(
    (offer, index) =>
      candidates.findIndex((candidate) => candidate.player.id === offer.player.id || samePlayer(candidate.player, offer.player)) === index,
  );
  const rankedCandidates = [...uniqueCandidates].sort(
    (left, right) =>
      hardMixedOfferSortScore(left, seed) - hardMixedOfferSortScore(right, seed),
  );
  const selected: DraftOffer[] = [];
  const clubCounts: Record<string, number> = {};

  function addOffer(offer: DraftOffer, maxSameClub: number) {
    if (selected.length >= DRAFT_PACK_SIZE) {
      return;
    }

    if (selected.some((item) => item.player.id === offer.player.id || samePlayer(item.player, offer.player))) {
      return;
    }

    const sameClub = clubCounts[offer.player.club] ?? 0;
    if (sameClub >= maxSameClub) {
      return;
    }

    selected.push(offer);
    clubCounts[offer.player.club] = sameClub + 1;
  }

  for (const offer of rankedCandidates) {
    addOffer(offer, 1);
  }

  for (const offer of rankedCandidates) {
    addOffer(offer, PACK_MAX_SAME_CLUB);
  }

  for (const offer of rankedCandidates) {
    addOffer(offer, Number.POSITIVE_INFINITY);
  }

  return selected.slice(0, DRAFT_PACK_SIZE);
}

function hardMixedOfferSortScore(offer: DraftOffer, seed: string) {
  const densityFactor = modernGlobalClubDensityFactor(offer.player, "draft-hard");
  const denseClubPenalty = Math.max(0, 1 - densityFactor) * 0.25;
  return seededNumber(`${seed}-${offer.slot.id}-${offer.player.id}`) + denseClubPenalty;
}

function getModePool(mode: GameMode, forcedClub?: GameMode["clubs"][number]) {
  if (mode.id === "fonte-2022") {
    return players.filter((player) => player.source?.name === "StatBunker" && player.season === "2022-23");
  }

  if (isModernMode(mode.id)) {
    return players.filter(
      (player) =>
        mode.clubs.includes(player.club) &&
        mode.eras.includes(player.era) &&
        seasonStartYear(player.season) > 2010,
    );
  }

  return players.filter((player) => mode.clubs.includes(player.club) && mode.eras.includes(player.era));
}

function isModernMode(modeId: string) {
  return modeId === "draft" || modeId === "draft-hard";
}

function isModernHardMode(mode?: GameMode) {
  return mode?.id === "draft-hard";
}

function isNostalgiaMode(modeId?: string) {
  return modeId === "nostalgia" || modeId === "nostalgia-hard";
}

function isProvinciaMode(modeId?: string) {
  return modeId === "provincia" || modeId === "provincia-medium";
}

function getNostalgiaShape(mode?: GameMode) {
  if (mode?.id === "nostalgia-hard") {
    return {
      minPre2000Picks: NOSTALGIA_HARD_MIN_PRE_2000_PICKS,
      max2000Picks: NOSTALGIA_HARD_MAX_2000_PICKS,
      minSeasons: NOSTALGIA_HARD_MIN_SEASONS,
      maxSameClubSeasonPicks: NOSTALGIA_HARD_MAX_SAME_CLUB_SEASON_PICKS,
    };
  }

  return {
    minPre2000Picks: NOSTALGIA_MIN_PRE_2000_PICKS,
    max2000Picks: NOSTALGIA_MAX_2000_PICKS,
    minSeasons: NOSTALGIA_MIN_SEASONS,
    maxSameClubSeasonPicks: NOSTALGIA_MAX_SAME_CLUB_SEASON_PICKS,
  };
}

function getProvinciaShape(mode?: GameMode) {
  if (mode?.id === "provincia-medium") {
    return {
      minCultBandPicks: PROVINCIA_MEDIUM_MIN_CULT_BAND_PICKS,
      maxIconPicks: PROVINCIA_MEDIUM_MAX_ICON_PICKS,
      minGrinderPicks: PROVINCIA_MEDIUM_MIN_GRINDER_PICKS,
      minClubs: PROVINCIA_MIN_CLUBS,
      maxSameClubSeasonPicks: PROVINCIA_MEDIUM_MAX_SAME_CLUB_SEASON_PICKS,
    };
  }

  return {
    minCultBandPicks: PROVINCIA_MIN_CULT_BAND_PICKS,
    maxIconPicks: PROVINCIA_MAX_ICON_PICKS,
    minGrinderPicks: PROVINCIA_MIN_GRINDER_PICKS,
    minClubs: PROVINCIA_MIN_CLUBS,
    maxSameClubSeasonPicks: PROVINCIA_MAX_SAME_CLUB_SEASON_PICKS,
  };
}

function seasonStartYear(season: string) {
  const match = /^(\d{4})/.exec(season);
  return match ? Number(match[1]) : 0;
}

function canCompleteFutureSlots(
  pool: PlayerSeason[],
  pickedIds: Set<string>,
  pickedNames: Set<string>,
  candidate: PlayerSeason,
  candidateSlot: LineupSlot,
  draftRound: number,
  formationSlots: LineupSlot[],
  mode: GameMode,
  picks: DraftPick[],
  useFormationOrderFallback: boolean,
) {
  const filledSlotIds = new Set(picks.map((pick) => pick.slot.id));
  filledSlotIds.add(candidateSlot.id);
  const futureSlots = useFormationOrderFallback
    ? formationSlots.slice(draftRound + 1)
    : formationSlots.filter((slot) => !filledSlotIds.has(slot.id));
  const reservedIds = new Set([...pickedIds, candidate.id]);
  const reservedNames = new Set([...pickedNames, normalizeName(candidate.name)]);
  const cacheKey = [
    mode.id,
    candidateSlot.id,
    futureSlots.map((slot) => slot.id).join(","),
    [...reservedIds].sort().join(","),
    [...reservedNames].sort().join(","),
  ].join("::");
  const cached = assignmentFeasibilityCache.get(cacheKey);

  if (cached !== undefined) {
    return cached;
  }

  const reservedPlayers = uniquePlayersByName([
    ...picks.map((pick) => pick.player),
    ...pool.filter((player) => pickedIds.has(player.id)),
    candidate,
  ]);
  const candidates = pool.filter((player) => !reservedIds.has(player.id) && !reservedNames.has(normalizeName(player.name)));

  if (futureSlots.length > 2) {
    const canCompleteQuickly = hasFastRoleAndModeCoverage(futureSlots, candidates, mode, reservedPlayers);
    assignmentFeasibilityCache.set(cacheKey, canCompleteQuickly);
    return canCompleteQuickly;
  }

  const canComplete = canAssignSlots(futureSlots, candidates, mode, reservedPlayers);
  assignmentFeasibilityCache.set(cacheKey, canComplete);
  return canComplete;
}

function hasFastRoleAndModeCoverage(slots: LineupSlot[], candidates: PlayerSeason[], mode: GameMode, seedPlayers: PlayerSeason[]) {
  const uniqueCandidates = uniquePlayersByName(candidates);

  for (const role of ["POR", "DIF", "CEN", "ATT"] as const) {
    const required = slots.filter((slot) => slot.role === role).length;
    if (required > 0 && uniqueCandidates.filter((player) => player.roles.includes(role)).length < required) {
      return false;
    }
  }

  if (isNostalgiaMode(mode.id)) {
    const nostalgiaShape = getNostalgiaShape(mode);
    const remainingOld = Math.max(0, nostalgiaShape.minPre2000Picks - seedPlayers.filter(isPre2000NostalgiaCard).length);
    const remaining2000Allowance = nostalgiaShape.max2000Picks - seedPlayers.filter(is2000sNostalgiaCameo).length;
    const seedSeasons = new Set(seedPlayers.map((player) => player.season));
    const possibleNewSeasons = new Set(uniqueCandidates.filter((player) => !seedSeasons.has(player.season)).map((player) => player.season));
    const overClubSeasonCap = Object.values(countBy(seedPlayers.map((player) => `${player.club}-${player.season}`))).some(
      (count) => count > nostalgiaShape.maxSameClubSeasonPicks,
    );

    return (
      !overClubSeasonCap &&
      remaining2000Allowance >= 0 &&
      uniqueCandidates.filter(isPre2000NostalgiaCard).length >= remainingOld &&
      seedSeasons.size + possibleNewSeasons.size >= Math.min(nostalgiaShape.minSeasons, seedPlayers.length + slots.length)
    );
  }

  if (isProvinciaMode(mode.id)) {
    const provinciaShape = getProvinciaShape(mode);
    const remainingCult = Math.max(0, provinciaShape.minCultBandPicks - seedPlayers.filter(isProvinciaCultBandCard).length);
    const remainingGrinders = Math.max(0, provinciaShape.minGrinderPicks - seedPlayers.filter(isProvinciaGrinderCard).length);
    const iconAllowance = provinciaShape.maxIconPicks - seedPlayers.filter(isProvinciaIconCard).length;
    const seedClubs = new Set(seedPlayers.map((player) => player.club));
    const possibleNewClubs = new Set(uniqueCandidates.filter((player) => !seedClubs.has(player.club)).map((player) => player.club));
    const overClubSeasonCap = Object.values(countBy(seedPlayers.map((player) => `${player.club}-${player.season}`))).some(
      (count) => count > provinciaShape.maxSameClubSeasonPicks,
    );

    return (
      !overClubSeasonCap &&
      iconAllowance >= 0 &&
      uniqueCandidates.filter(isProvinciaCultBandCard).length >= remainingCult &&
      uniqueCandidates.filter(isProvinciaGrinderCard).length >= remainingGrinders &&
      seedClubs.size + possibleNewClubs.size >= Math.min(provinciaShape.minClubs, seedPlayers.length + slots.length)
    );
  }

  if (mode.id === "draft") {
    const clubCounts = countBy(seedPlayers.map((player) => player.club));
    const clubSeasonCounts = countBy(seedPlayers.map((player) => `${player.club}-${player.season}`));
    const seedClubs = new Set(seedPlayers.map((player) => player.club));
    const possibleNewClubs = new Set(uniqueCandidates.filter((player) => !seedClubs.has(player.club)).map((player) => player.club));
    const overClubCap = Object.values(clubCounts).some((count) => count > MODERN_DRAFT_MAX_SAME_CLUB_PICKS);
    const overClubSeasonCap = Object.values(clubSeasonCounts).some((count) => count > MODERN_DRAFT_MAX_SAME_CLUB_SEASON_PICKS);

    return (
      !overClubCap &&
      !overClubSeasonCap &&
      seedClubs.size + possibleNewClubs.size >= Math.min(MODERN_DRAFT_MIN_CLUBS, seedPlayers.length + slots.length)
    );
  }

  if (isModernHardMode(mode)) {
    const remainingBalance = Math.max(0, MODERN_HARD_MIN_BALANCE_PICKS - seedPlayers.filter(isModernHardBalanceCard).length);
    const headlineAllowance = MODERN_HARD_MAX_HEADLINE_PICKS - seedPlayers.filter(isModernHardHeadlineCard).length;
    const seedClubs = new Set(seedPlayers.map((player) => player.club));
    const possibleNewClubs = new Set(uniqueCandidates.filter((player) => !seedClubs.has(player.club)).map((player) => player.club));
    const overClubCap = Object.values(countBy(seedPlayers.map((player) => player.club))).some(
      (count) => count > MODERN_HARD_MAX_SAME_CLUB_PICKS,
    );
    const overClubSeasonCap = Object.values(countBy(seedPlayers.map((player) => `${player.club}-${player.season}`))).some(
      (count) => count > MODERN_HARD_MAX_SAME_CLUB_SEASON_PICKS,
    );

    return (
      !overClubCap &&
      !overClubSeasonCap &&
      headlineAllowance >= 0 &&
      uniqueCandidates.filter(isModernHardBalanceCard).length >= remainingBalance &&
      seedClubs.size + possibleNewClubs.size >= Math.min(MODERN_HARD_MIN_CLUBS, seedPlayers.length + slots.length)
    );
  }

  return true;
}

function canAssignSlots(slots: LineupSlot[], candidates: PlayerSeason[], mode?: GameMode, seedPlayers: PlayerSeason[] = []) {
  const orderedSlots = [...slots].sort((left, right) => roleCount(candidates, left.role) - roleCount(candidates, right.role));
  const usedNames = new Set<string>();
  const usedIds = new Set<string>();
  const assignedPlayers: PlayerSeason[] = [];
  const memo = new Set<string>();
  const seedOldCount = seedPlayers.filter(isPre2000NostalgiaCard).length;
  const seed2000Count = seedPlayers.filter(is2000sNostalgiaCameo).length;
  const seedSeasons = new Set(seedPlayers.map((player) => player.season));
  const seedProvinciaCultCount = seedPlayers.filter(isProvinciaCultBandCard).length;
  const seedProvinciaIconCount = seedPlayers.filter(isProvinciaIconCard).length;
  const seedProvinciaGrinderCount = seedPlayers.filter(isProvinciaGrinderCard).length;
  const seedProvinciaClubs = new Set(seedPlayers.map((player) => player.club));
  const seedModernDraftClubs = new Set(seedPlayers.map((player) => player.club));
  const seedModernHardBalanceCount = seedPlayers.filter(isModernHardBalanceCard).length;
  const seedModernHardHeadlineCount = seedPlayers.filter(isModernHardHeadlineCard).length;
  const seedModernHardClubs = new Set(seedPlayers.map((player) => player.club));
  const nostalgiaShape = getNostalgiaShape(mode);
  const provinciaShape = getProvinciaShape(mode);
  const requiredOldCount = isNostalgiaMode(mode?.id)
    ? Math.min(nostalgiaShape.minPre2000Picks, seedPlayers.length + slots.length)
    : 0;
  const max2000Count = isNostalgiaMode(mode?.id)
    ? Math.min(nostalgiaShape.max2000Picks, seedPlayers.length + slots.length)
    : Number.POSITIVE_INFINITY;
  const requiredNostalgiaSeasonCount = isNostalgiaMode(mode?.id)
    ? Math.min(nostalgiaShape.minSeasons, seedPlayers.length + slots.length)
    : 0;
  const requiredProvinciaCultCount = isProvinciaMode(mode?.id)
    ? Math.min(provinciaShape.minCultBandPicks, seedPlayers.length + slots.length)
    : 0;
  const maxProvinciaIconCount = isProvinciaMode(mode?.id)
    ? Math.min(provinciaShape.maxIconPicks, seedPlayers.length + slots.length)
    : Number.POSITIVE_INFINITY;
  const requiredProvinciaGrinderCount = isProvinciaMode(mode?.id)
    ? Math.min(provinciaShape.minGrinderPicks, seedPlayers.length + slots.length)
    : 0;
  const requiredProvinciaClubCount = isProvinciaMode(mode?.id)
    ? Math.min(provinciaShape.minClubs, seedPlayers.length + slots.length)
    : 0;
  const requiredModernDraftClubCount = mode?.id === "draft"
    ? Math.min(MODERN_DRAFT_MIN_CLUBS, seedPlayers.length + slots.length)
    : 0;
  const requiredModernHardBalanceCount = isModernHardMode(mode)
    ? Math.min(MODERN_HARD_MIN_BALANCE_PICKS, seedPlayers.length + slots.length)
    : 0;
  const maxModernHardHeadlineCount = isModernHardMode(mode)
    ? Math.min(MODERN_HARD_MAX_HEADLINE_PICKS, seedPlayers.length + slots.length)
    : Number.POSITIVE_INFINITY;
  const requiredModernHardClubCount = isModernHardMode(mode)
    ? Math.min(MODERN_HARD_MIN_CLUBS, seedPlayers.length + slots.length)
    : 0;

  function assign(index: number): boolean {
    if (isNostalgiaMode(mode?.id)) {
      const currentOldCount = seedOldCount + assignedPlayers.filter(isPre2000NostalgiaCard).length;
      const current2000Count = seed2000Count + assignedPlayers.filter(is2000sNostalgiaCameo).length;
      const currentClubSeasonCounts = countBy([...seedPlayers, ...assignedPlayers].map((player) => `${player.club}-${player.season}`));
      const currentSeasons = new Set([...seedSeasons, ...assignedPlayers.map((player) => player.season)]);
      const remainingSlots = orderedSlots.length - index;

      if (currentOldCount + remainingSlots < requiredOldCount) {
        return false;
      }

      if (current2000Count > max2000Count) {
        return false;
      }

      if (Object.values(currentClubSeasonCounts).some((count) => count > nostalgiaShape.maxSameClubSeasonPicks)) {
        return false;
      }

      if (
        currentSeasons.size +
          countPotentialNewValues(candidates, usedIds, usedNames, currentSeasons, (player) => player.season) <
        requiredNostalgiaSeasonCount
      ) {
        return false;
      }
    }

    if (isProvinciaMode(mode?.id)) {
      const currentCultCount = seedProvinciaCultCount + assignedPlayers.filter(isProvinciaCultBandCard).length;
      const currentIconCount = seedProvinciaIconCount + assignedPlayers.filter(isProvinciaIconCard).length;
      const currentGrinderCount = seedProvinciaGrinderCount + assignedPlayers.filter(isProvinciaGrinderCard).length;
      const currentClubSeasonCounts = countBy([...seedPlayers, ...assignedPlayers].map((player) => `${player.club}-${player.season}`));
      const currentClubs = new Set([...seedProvinciaClubs, ...assignedPlayers.map((player) => player.club)]);
      const remainingSlots = orderedSlots.length - index;

      if (currentCultCount + remainingSlots < requiredProvinciaCultCount) {
        return false;
      }

      if (currentGrinderCount + remainingSlots < requiredProvinciaGrinderCount) {
        return false;
      }

      if (currentIconCount > maxProvinciaIconCount) {
        return false;
      }

      if (Object.values(currentClubSeasonCounts).some((count) => count > provinciaShape.maxSameClubSeasonPicks)) {
        return false;
      }

      if (
        currentClubs.size +
          countPotentialNewValues(candidates, usedIds, usedNames, currentClubs, (player) => player.club) <
        requiredProvinciaClubCount
      ) {
        return false;
      }
    }

    if (mode?.id === "draft") {
      const currentPlayers = [...seedPlayers, ...assignedPlayers];
      const currentClubCounts = countBy(currentPlayers.map((player) => player.club));
      const currentClubSeasonCounts = countBy(currentPlayers.map((player) => `${player.club}-${player.season}`));
      const currentClubs = new Set([...seedModernDraftClubs, ...assignedPlayers.map((player) => player.club)]);

      if (Object.values(currentClubCounts).some((count) => count > MODERN_DRAFT_MAX_SAME_CLUB_PICKS)) {
        return false;
      }

      if (Object.values(currentClubSeasonCounts).some((count) => count > MODERN_DRAFT_MAX_SAME_CLUB_SEASON_PICKS)) {
        return false;
      }

      if (
        currentClubs.size +
          countPotentialNewValues(candidates, usedIds, usedNames, currentClubs, (player) => player.club) <
        requiredModernDraftClubCount
      ) {
        return false;
      }
    }

    if (isModernHardMode(mode)) {
      const currentBalanceCount = seedModernHardBalanceCount + assignedPlayers.filter(isModernHardBalanceCard).length;
      const currentHeadlineCount = seedModernHardHeadlineCount + assignedPlayers.filter(isModernHardHeadlineCard).length;
      const currentPlayers = [...seedPlayers, ...assignedPlayers];
      const currentClubCounts = countBy(currentPlayers.map((player) => player.club));
      const currentClubSeasonCounts = countBy(currentPlayers.map((player) => `${player.club}-${player.season}`));
      const currentClubs = new Set([...seedModernHardClubs, ...assignedPlayers.map((player) => player.club)]);
      const remainingSlots = orderedSlots.length - index;

      if (currentBalanceCount + remainingSlots < requiredModernHardBalanceCount) {
        return false;
      }

      if (currentHeadlineCount > maxModernHardHeadlineCount) {
        return false;
      }

      if (Object.values(currentClubCounts).some((count) => count > MODERN_HARD_MAX_SAME_CLUB_PICKS)) {
        return false;
      }

      if (Object.values(currentClubSeasonCounts).some((count) => count > MODERN_HARD_MAX_SAME_CLUB_SEASON_PICKS)) {
        return false;
      }

      if (
        currentClubs.size +
          countPotentialNewValues(candidates, usedIds, usedNames, currentClubs, (player) => player.club) <
        requiredModernHardClubCount
      ) {
        return false;
      }
    }

    if (index >= orderedSlots.length) {
      return satisfiesModeDraftShape([...seedPlayers, ...assignedPlayers], mode);
    }

    const memoKey = `${index}:${[...usedNames].sort().join("|")}`;
    if (memo.has(memoKey)) {
      return false;
    }

    const slot = orderedSlots[index];
    const eligible = candidates
      .filter((player) => isSlotCompatible(player, slot) && !usedIds.has(player.id) && !usedNames.has(normalizeName(player.name)))
      .sort(
        (left, right) =>
          modeAssignmentPriority(right, mode) - modeAssignmentPriority(left, mode) ||
          roleFlex(left) - roleFlex(right) ||
          right.score - left.score,
      )
      .slice(0, MAX_ASSIGNMENT_BRANCHES_PER_SLOT);

    for (const player of eligible) {
      usedIds.add(player.id);
      usedNames.add(normalizeName(player.name));
      assignedPlayers.push(player);
      if (assign(index + 1)) {
        return true;
      }
      assignedPlayers.pop();
      usedIds.delete(player.id);
      usedNames.delete(normalizeName(player.name));
    }

    memo.add(memoKey);
    return false;
  }

  return assign(0);
}

function satisfiesModeDraftShape(lineup: PlayerSeason[], mode?: GameMode) {
  if (!mode) {
    return true;
  }

  if (mode.id === "draft") {
    const clubCounts = countBy(lineup.map((player) => player.club));
    const clubSeasonCounts = countBy(lineup.map((player) => `${player.club}-${player.season}`));

    return (
      new Set(lineup.map((player) => player.club)).size >= Math.min(MODERN_DRAFT_MIN_CLUBS, lineup.length) &&
      Object.values(clubCounts).every((count) => count <= MODERN_DRAFT_MAX_SAME_CLUB_PICKS) &&
      Object.values(clubSeasonCounts).every((count) => count <= MODERN_DRAFT_MAX_SAME_CLUB_SEASON_PICKS)
    );
  }

  if (isNostalgiaMode(mode.id)) {
    const nostalgiaShape = getNostalgiaShape(mode);
    const clubSeasonCounts = countBy(lineup.map((player) => `${player.club}-${player.season}`));

    return (
      lineup.filter(isPre2000NostalgiaCard).length >= Math.min(nostalgiaShape.minPre2000Picks, lineup.length) &&
      lineup.filter(is2000sNostalgiaCameo).length <= Math.min(nostalgiaShape.max2000Picks, lineup.length) &&
      new Set(lineup.map((player) => player.season)).size >= Math.min(nostalgiaShape.minSeasons, lineup.length) &&
      Object.values(clubSeasonCounts).every((count) => count <= nostalgiaShape.maxSameClubSeasonPicks)
    );
  }

  if (isProvinciaMode(mode.id)) {
    const provinciaShape = getProvinciaShape(mode);
    const clubSeasonCounts = countBy(lineup.map((player) => `${player.club}-${player.season}`));

    return (
      lineup.filter(isProvinciaCultBandCard).length >= Math.min(provinciaShape.minCultBandPicks, lineup.length) &&
      lineup.filter(isProvinciaIconCard).length <= Math.min(provinciaShape.maxIconPicks, lineup.length) &&
      lineup.filter(isProvinciaGrinderCard).length >= Math.min(provinciaShape.minGrinderPicks, lineup.length) &&
      new Set(lineup.map((player) => player.club)).size >= Math.min(provinciaShape.minClubs, lineup.length) &&
      Object.values(clubSeasonCounts).every((count) => count <= provinciaShape.maxSameClubSeasonPicks)
    );
  }

  if (isModernHardMode(mode)) {
    const clubCounts = countBy(lineup.map((player) => player.club));
    const clubSeasonCounts = countBy(lineup.map((player) => `${player.club}-${player.season}`));

    return (
      lineup.filter(isModernHardHeadlineCard).length <= Math.min(MODERN_HARD_MAX_HEADLINE_PICKS, lineup.length) &&
      lineup.filter(isModernHardBalanceCard).length >= Math.min(MODERN_HARD_MIN_BALANCE_PICKS, lineup.length) &&
      new Set(lineup.map((player) => player.club)).size >= Math.min(MODERN_HARD_MIN_CLUBS, lineup.length) &&
      Object.values(clubCounts).every((count) => count <= MODERN_HARD_MAX_SAME_CLUB_PICKS) &&
      Object.values(clubSeasonCounts).every((count) => count <= MODERN_HARD_MAX_SAME_CLUB_SEASON_PICKS)
    );
  }

  return true;
}

function modeAssignmentPriority(player: PlayerSeason, mode?: GameMode) {
  if (isNostalgiaMode(mode?.id)) {
    const cameoPenalty = mode?.id === "nostalgia-hard" ? -5 : -2;
    return isPre2000NostalgiaCard(player) ? 8 : is2000sNostalgiaCameo(player) ? cameoPenalty : -8;
  }

  if (isProvinciaMode(mode?.id)) {
    return (isProvinciaCultBandCard(player) ? 8 : isProvinciaIconCard(player) ? -3 : 1) + (isProvinciaGrinderCard(player) ? 3 : 0);
  }

  if (isModernHardMode(mode)) {
    return (isModernHardBalanceCard(player) ? 7 : 0) + (isModernHardHeadlineCard(player) ? -5 : 2);
  }

  return 0;
}

function isPre2000NostalgiaCard(player: PlayerSeason) {
  return player.era === "anni70" || player.era === "anni80" || player.era === "anni90";
}

function is2000sNostalgiaCameo(player: PlayerSeason) {
  return player.era === "anni2000";
}

function isProvinciaCultBandCard(player: PlayerSeason) {
  return player.score >= 83 && player.score <= 92;
}

function isProvinciaIconCard(player: PlayerSeason) {
  return player.score >= 93;
}

function isProvinciaGrinderCard(player: PlayerSeason) {
  const cached = provinciaGrinderCache.get(player.id);

  if (cached !== undefined) {
    return cached;
  }

  const isGrinder = player.score <= 86 && getPlayerProfile(player).workRate >= 88;
  provinciaGrinderCache.set(player.id, isGrinder);
  return isGrinder;
}

function isModernHardHeadlineCard(player: PlayerSeason) {
  return player.score >= 93;
}

function isModernHardBalanceCard(player: PlayerSeason) {
  const profile = getPlayerProfile(player);
  return player.score <= 91 && (profile.workRate >= 86 || profile.defending >= 84 || profile.passing >= 86);
}

function roleCount(candidates: PlayerSeason[], role: LineupSlot["role"]) {
  return candidates.filter((player) => player.roles.includes(role)).length;
}

function roleFlex(player: PlayerSeason) {
  return getEffectivePositionTags(player).length;
}

function countPotentialNewValues<T>(
  candidates: PlayerSeason[],
  usedIds: Set<string>,
  usedNames: Set<string>,
  currentValues: Set<T>,
  getValue: (player: PlayerSeason) => T,
) {
  const values = new Set<T>();

  for (const player of candidates) {
    if (usedIds.has(player.id) || usedNames.has(normalizeName(player.name))) {
      continue;
    }

    const value = getValue(player);
    if (!currentValues.has(value)) {
      values.add(value);
    }
  }

  return values.size;
}

export function getCandidateFit(prompt: DraftPrompt, player: PlayerSeason, lang: Language = "it") {
  if (player.club === prompt.club && player.era === prompt.era) {
    return translations[lang].fitPerfect;
  }

  if (player.club === prompt.club) {
    return translations[lang].fitClub;
  }

  if (player.era === prompt.era) {
    return translations[lang].fitEra;
  }

  return translations[lang].fitWild;
}

function getPackPlan(modeId: string, round: number, lang: Language = "it") {
  const lateRound = round >= 7;

  const keyMap: Record<string, Record<string, string>> = {
    provincia: {
      modeClub: "packProvincia",
      spineStarter: "packAvvioOssatura",
      chemistry: "packIntesa",
      spine: "packOssatura",
      creator: "packCreatore",
      icon: "packLeggendaFuori",
      gem: "packTalentoNascosto",
      balance: "packEquilibrio",
      sacrifice: "packSacrificio",
      era: "packStessaEpoca",
      wild: "packJollyPazzo",
    },
    "fonte-2022": {
      exact: "packExact",
      starts: "packTitolareFisso",
      goals: "packImpattoGol",
      chemistry: "packIntesa",
      spine: "packOssatura",
      wild: "packRotazione",
    },
    draft: {
      club: "packStoriaClub",
      spineStarter: "packAvvioOssatura",
      chemistry: "packIntesaBandiera",
      spine: "packOssatura",
      creator: "packCreatore",
      balance: "packEquilibrio",
      sacrifice: "packSacrificio",
      icon: "packIdoloAssoluto",
      era: "packEpocaGiusta",
      goals: "packImpatto",
      wild: "packVersioneCult",
    },
    "draft-hard": {
      club: "packStoriaClub",
      spineStarter: "packAvvioOssatura",
      chemistry: "packIntesaBandiera",
      spine: "packOssatura",
      creator: "packCreatore",
      balance: "packEquilibrio",
      sacrifice: "packSacrificio",
      icon: "packIdoloAssoluto",
      era: "packEpocaGiusta",
      goals: "packImpatto",
      wild: "packVersioneCult",
    },
    nostalgia: {
      exact: "packExact",
      spineStarter: "packAvvioOssatura",
      icon: "packIcona",
      chemistry: "packIntesa",
      spine: "packOssatura",
      creator: "packCreatore",
      balance: "packEquilibrio",
      sacrifice: "packSacrificio",
      era: "packStessaEpoca",
      wild: "packCultHero",
    },
    default: {
      exact: "packExact",
      spineStarter: "packAvvioOssatura",
      chemistry: "packIntesa",
      spine: "packOssatura",
      creator: "packCreatore",
      balance: "packEquilibrio",
      sacrifice: "packSacrificio",
      club: "packStoriaClub",
      icon: "packFuoriclasse",
      era: "packStessaEpoca",
      wild: "packJolly",
    }
  };

  const plan = isProvinciaMode(modeId)
    ? [
        { type: round === 0 ? "spineStarter" : "modeClub" },
        { type: round % 3 === 1 ? "sacrifice" : "chemistry" },
        { type: "spine" },
        { type: round % 2 === 0 ? "balance" : "creator" },
        { type: "wild" },
      ]
    : modeId === "fonte-2022"
    ? [
        { type: "exact" },
        { type: "starts" },
        { type: "goals" },
        { type: "spine" },
        { type: "chemistry" },
      ]
    : modeId === "draft"
    ? [
        { type: round % 4 === 0 ? "club" : "era" },
        { type: round === 0 ? "spineStarter" : round % 2 === 0 ? "balance" : "creator" },
        { type: round % 3 === 2 ? "sacrifice" : "chemistry" },
        { type: round % 2 === 0 ? "balance" : "creator" },
        { type: lateRound && round % 2 === 1 ? "icon" : "club" },
      ]
    : modeId === "draft-hard"
    ? [
        { type: round === 0 ? "spineStarter" : "balance" },
        { type: round % 3 === 0 ? "sacrifice" : "spine" },
        { type: round % 2 === 0 ? "chemistry" : "creator" },
        { type: round % 4 === 1 ? "era" : "club" },
        { type: lateRound && round % 3 === 2 ? "icon" : "wild" },
      ]
    : isNostalgiaMode(modeId)
    ? [
        { type: round === 0 ? "spineStarter" : round % 4 === 0 ? "icon" : "exact" },
        { type: "spine" },
        { type: round % 3 === 1 ? "sacrifice" : "era" },
        { type: round % 2 === 0 ? "balance" : "chemistry" },
        { type: round >= 6 ? "creator" : "wild" },
      ]
    : [
        { type: "exact" },
        { type: "chemistry" },
        { type: "spine" },
        { type: "creator" },
        { type: "club" },
      ];

  const modeKey = isProvinciaMode(modeId) ? "provincia" : isNostalgiaMode(modeId) ? "nostalgia" : modeId;
  const map = keyMap[modeKey] || keyMap.default;
  return plan.map((item) => {
    const key = map[item.type] || item.type;
    const label = ((translations[lang] as any)[key] as string) || key;
    return { type: item.type, label };
  });
}

function pickForLane(
  available: PlayerSeason[],
  selected: DraftOffer[],
  lane: { type: string; label: string },
  mode: GameMode,
  prompt: DraftPrompt,
  picks: DraftPick[],
  packSeed = "base",
) {
  const selectedIds = new Set(selected.map((offer) => offer.player.id));
  const selectedNames = new Set(selected.map((offer) => normalizeName(offer.player.name)));
  const unpicked = available.filter((player) => !selectedIds.has(player.id) && !selectedNames.has(normalizeName(player.name)));
  const shapeEligible = getModeShapeEligibleCandidates(unpicked, mode.id, picks);
  const chemistryClubs = new Set(picks.map((pick) => pick.player.club));
  const chemistryCountries = new Set(picks.map((pick) => pick.player.country));
  const chemistryEras = new Set(picks.map((pick) => pick.player.era));

  if (shapeEligible.length === 0) {
    return undefined;
  }

  const gatedUnpicked = shapeEligible.filter((player) => passesEliteOfferGate(player, lane.type, mode.id, prompt, picks, packSeed));
  const nonEliteFallback = shapeEligible.filter((player) => player.score < ELITE_OFFER_SCORE_FLOOR && getIconPeak(player) < ICON_OFFER_PEAK_FLOOR);
  const filterBase = gatedUnpicked.length > 0 ? gatedUnpicked : nonEliteFallback.length > 0 ? nonEliteFallback : shapeEligible;

  const filtered = filterBase.filter((player) => {
    switch (lane.type) {
      case "exact":
        return player.club === prompt.club && player.era === prompt.era;
      case "club":
      case "modeClub":
        return player.club === prompt.club;
      case "era":
        return player.era === prompt.era;
      case "chemistry":
        return (
          chemistryClubs.has(player.club) ||
          chemistryCountries.has(player.country) ||
          chemistryEras.has(player.era) ||
          sharedProfileTags(player, picks) > 0
        );
      case "spine":
        return seasonSpineScore(player, picks) > 0;
      case "spineStarter":
        return sourcedSeasonBlockScore(player, mode) >= 8;
      case "creator":
        return (player.assists ?? 0) >= 5 || getPlayerProfile(player).creativity >= 90 || getPlayerProfile(player).passing >= 90;
      case "balance":
        return getPlayerProfile(player).workRate >= 84 || getPlayerProfile(player).defending >= 84 || getPlayerProfile(player).passing >= 84;
      case "sacrifice":
        return player.score <= 88 && getPlayerProfile(player).workRate >= 86;
      case "icon": {
        const profile = getPlayerProfile(player);
        return getIconPeak(player, profile) >= ICON_LANE_MINIMUM_PEAK;
      }
      case "gem":
        return getPlayerProfile(player).legacy < 94 && (player.club === prompt.club || mode.clubs.includes(player.club));
      case "starts":
        return (player.starts ?? 0) >= 30 || (player.appearances ?? 0) >= 34;
      case "goals":
        return (player.goals ?? 0) >= 8 || getPlayerProfile(player).finishing >= 88;
      default:
        return true;
    }
  });

  const candidates = filtered.length > 0 ? filtered : lane.type === "spine" ? [] : shapeEligible;
  const exposureCappedCandidates = applyPackEliteExposureBudget(candidates, selected);
  const teamCappedCandidates = applyPackTeamExposureBudget(exposureCappedCandidates, selected, mode.id);
  const clubBalancedCandidates = applyLaneClubDensityCap(teamCappedCandidates, mode.id, lane.type, packSeed);
  return weightedPick(clubBalancedCandidates, `${packSeed}-${mode.id}-${prompt.label}-${lane.type}-${picks.length}`, (player) =>
    laneWeight(player, lane.type, prompt, picks, mode.id, clubBalancedCandidates),
  );
}

function applyLaneClubDensityCap(candidates: PlayerSeason[], modeId: string, laneType: string, seed: string) {
  if (!isModernMode(modeId) || candidates.length <= 5 || laneType === "exact" || laneType === "club" || laneType === "modeClub") {
    return candidates;
  }

  const maxPerClub = modeId === "draft-hard" ? 1 : 2;
  const grouped = candidates.reduce<Record<string, PlayerSeason[]>>((groups, player) => {
    groups[player.club] = groups[player.club] ?? [];
    groups[player.club].push(player);
    return groups;
  }, {});
  const capped = Object.entries(grouped).flatMap(([club, group]) => shuffled(group, `${seed}-${modeId}-${laneType}-${club}`).slice(0, maxPerClub));

  return capped.length > 0 ? capped : candidates;
}

function getModeShapeEligibleCandidates(candidates: PlayerSeason[], modeId: string, picks: DraftPick[]) {
  if (modeId === "draft") {
    return applyModernDraftLineupDiversity(candidates, picks);
  }

  const clubSeasonCap = getSameClubSeasonCapForMode(modeId);
  const clubSeasonEligible = clubSeasonCap
    ? applySameClubSeasonLineupCap(candidates, picks, clubSeasonCap)
    : candidates;

  if (modeId !== "draft-hard") {
    return clubSeasonEligible;
  }

  const remainingSlotsIncludingThisPick = Math.max(1, 11 - picks.length);
  const pickedPlayers = picks.map((pick) => pick.player);
  const pickedClubs = new Set(pickedPlayers.map((player) => player.club));
  const clubCounts = countBy(pickedPlayers.map((player) => player.club));
  const missingClubs = Math.max(0, MODERN_HARD_MIN_CLUBS - pickedClubs.size);
  const missingBalance = Math.max(0, MODERN_HARD_MIN_BALANCE_PICKS - pickedPlayers.filter(isModernHardBalanceCard).length);
  const headlineAllowance = MODERN_HARD_MAX_HEADLINE_PICKS - pickedPlayers.filter(isModernHardHeadlineCard).length;

  let eligible = clubSeasonEligible;

  if (headlineAllowance <= 0) {
    eligible = eligible.filter((player) => !isModernHardHeadlineCard(player));
  }

  const outsideSaturatedClubs = eligible.filter((player) => (clubCounts[player.club] ?? 0) < MODERN_HARD_MAX_SAME_CLUB_PICKS);
  if (outsideSaturatedClubs.length > 0) {
    eligible = outsideSaturatedClubs;
  }

  if (missingBalance >= remainingSlotsIncludingThisPick) {
    eligible = eligible.filter(isModernHardBalanceCard);
  }

  if (missingClubs >= remainingSlotsIncludingThisPick) {
    eligible = eligible.filter((player) => !pickedClubs.has(player.club));
  }

  return eligible;
}

function getSameClubSeasonCapForMode(modeId: string) {
  if (modeId === "draft-hard") {
    return MODERN_HARD_MAX_SAME_CLUB_SEASON_PICKS;
  }

  if (modeId === "nostalgia-hard") {
    return NOSTALGIA_HARD_MAX_SAME_CLUB_SEASON_PICKS;
  }

  if (modeId === "nostalgia") {
    return NOSTALGIA_MAX_SAME_CLUB_SEASON_PICKS;
  }

  if (modeId === "provincia") {
    return PROVINCIA_MAX_SAME_CLUB_SEASON_PICKS;
  }

  if (modeId === "provincia-medium") {
    return PROVINCIA_MEDIUM_MAX_SAME_CLUB_SEASON_PICKS;
  }

  return 0;
}

function applySameClubSeasonLineupCap(candidates: PlayerSeason[], picks: DraftPick[], cap: number) {
  const clubSeasonCounts = countBy(picks.map((pick) => `${pick.player.club}-${pick.player.season}`));
  const capped = candidates.filter((player) => (clubSeasonCounts[`${player.club}-${player.season}`] ?? 0) < cap);
  return capped.length > 0 ? capped : candidates;
}

function applyModernDraftLineupDiversity(candidates: PlayerSeason[], picks: DraftPick[]) {
  const remainingSlotsIncludingThisPick = Math.max(1, 11 - picks.length);
  const pickedPlayers = picks.map((pick) => pick.player);
  const pickedClubs = new Set(pickedPlayers.map((player) => player.club));
  const clubCounts = countBy(pickedPlayers.map((player) => player.club));
  const clubSeasonCounts = countBy(pickedPlayers.map((player) => `${player.club}-${player.season}`));
  const missingClubs = Math.max(0, MODERN_DRAFT_MIN_CLUBS - pickedClubs.size);

  let eligible = candidates;

  const outsideSaturatedClubSeasons = eligible.filter(
    (player) => (clubSeasonCounts[`${player.club}-${player.season}`] ?? 0) < MODERN_DRAFT_MAX_SAME_CLUB_SEASON_PICKS,
  );
  if (outsideSaturatedClubSeasons.length > 0) {
    eligible = outsideSaturatedClubSeasons;
  }

  const outsideSaturatedClubs = eligible.filter(
    (player) => (clubCounts[player.club] ?? 0) < MODERN_DRAFT_MAX_SAME_CLUB_PICKS,
  );
  if (outsideSaturatedClubs.length > 0) {
    eligible = outsideSaturatedClubs;
  }

  if (missingClubs >= remainingSlotsIncludingThisPick) {
    const newClubCandidates = eligible.filter((player) => !pickedClubs.has(player.club));
    if (newClubCandidates.length > 0) {
      eligible = newClubCandidates;
    }
  }

  return eligible;
}

function laneWeight(
  player: PlayerSeason,
  laneType: string,
  prompt: DraftPrompt,
  picks: DraftPick[],
  modeId: string,
  laneCandidates: PlayerSeason[] = [],
) {
  const profile = getPlayerProfile(player);
  const promptBoost = (player.club === prompt.club ? 16 : 0) + (player.era === prompt.era ? 10 : 0);
  const chemistryBoost = diminishingChemistryBoost(player, picks);
  const seasonSpineBoost = diminishingSeasonSpineBoost(player, picks);
  const profileWeight = profileWeightForLane(profile, laneType);
  const ratingWeight = laneType === "wild" || laneType === "sacrifice" ? profileWeight / 9 : laneType === "balance" ? profileWeight / 4.8 : profileWeight / 4;
  const sourceBoost = player.source || player.evidence?.length ? sourceBoostForLane(modeId, laneType) : 0;
  const curatedProfileBoost = profile.dataLevel === "curated-high" ? 4 : 0;
  const modeWeight = modeWeightAdjustment(player, profile, modeId, laneType);
  const modeShapeWeight = modeShapeWeightAdjustment(player, modeId, picks);
  const diversityWeight = modeDiversityWeightAdjustment(player, modeId, picks);
  const historicalVarietyWeight = modeHistoricalVarietyWeightAdjustment(player, modeId, picks);
  const rarity = getRarity(player);
  const eliteExposurePenalty = eliteExposurePenaltyForLane(player, rarity, laneType);
  const rarityNoise = seededNumber(`${player.id}-${laneType}`) * 9;
  const densityFactor = modernClubDensityFactor(player, laneCandidates, modeId);
  const globalClubFactor = modernGlobalClubDensityFactor(player, modeId);
  const nostalgiaClubFactor = nostalgiaGlobalClubDensityFactor(player, modeId);
  const nostalgiaNameFactor = nostalgiaDuplicateNameFactor(player, modeId);
  const rawWeight = ratingWeight + promptBoost + chemistryBoost + seasonSpineBoost + sourceBoost + curatedProfileBoost + modeWeight + modeShapeWeight + diversityWeight + historicalVarietyWeight + rarityNoise - eliteExposurePenalty;

  return Math.max(1, rawWeight * densityFactor * globalClubFactor * nostalgiaClubFactor * nostalgiaNameFactor);
}

function sourceBoostForLane(modeId: string, laneType: string) {
  if (isModernMode(modeId) && laneType !== "exact" && laneType !== "spine" && laneType !== "spineStarter") {
    return 2;
  }

  return 6;
}

function modernClubDensityFactor(player: PlayerSeason, laneCandidates: PlayerSeason[], modeId: string) {
  if (!isModernMode(modeId) || laneCandidates.length < 2) {
    return 1;
  }

  const clubCounts = countBy(laneCandidates.map((candidate) => candidate.club));
  const uniqueClubs = Math.max(1, Object.keys(clubCounts).length);
  const averageClubSize = laneCandidates.length / uniqueClubs;
  const clubSize = clubCounts[player.club] ?? 1;
  const densityFactor = averageClubSize / clubSize;

  return Math.max(MODERN_DENSE_CLUB_MIN_WEIGHT, Math.min(MODERN_DENSE_CLUB_MAX_WEIGHT, densityFactor));
}

function modernGlobalClubDensityFactor(player: PlayerSeason, modeId: string) {
  if (!isModernMode(modeId)) {
    return 1;
  }

  if (!modernGlobalClubDensityCache) {
    const modernPool = players.filter((candidate) => (candidate.era === "anni2010" || candidate.era === "moderna") && seasonStartYear(candidate.season) > 2010);
    const clubCounts = countBy(modernPool.map((candidate) => candidate.club));
    const uniqueClubs = Math.max(1, Object.keys(clubCounts).length);
    const averageClubSize = modernPool.length / uniqueClubs;
    modernGlobalClubDensityCache = Object.fromEntries(
      Object.entries(clubCounts).map(([club, count]) => [
        club,
        Math.max(MODERN_DENSE_CLUB_MIN_WEIGHT, Math.min(MODERN_DENSE_CLUB_MAX_WEIGHT, averageClubSize / count)),
      ]),
    );
  }

  const baseFactor = modernGlobalClubDensityCache[player.club] ?? 1;

  if (modeId === "draft-hard") {
    return Math.max(0.2, Math.min(1.45, baseFactor * 1.05));
  }

  return baseFactor;
}

function nostalgiaGlobalClubDensityFactor(player: PlayerSeason, modeId: string) {
  if (!isNostalgiaMode(modeId) || !isPre2000NostalgiaCard(player)) {
    return 1;
  }

  if (!nostalgiaGlobalClubDensityCache) {
    const nostalgiaPool = players.filter(isPre2000NostalgiaCard);
    const clubCounts = countBy(nostalgiaPool.map((candidate) => candidate.club));
    const uniqueClubs = Math.max(1, Object.keys(clubCounts).length);
    const averageClubSize = nostalgiaPool.length / uniqueClubs;
    nostalgiaGlobalClubDensityCache = Object.fromEntries(
      Object.entries(clubCounts).map(([club, count]) => [
        club,
        Math.max(NOSTALGIA_DENSE_CLUB_MIN_WEIGHT, Math.min(NOSTALGIA_DENSE_CLUB_MAX_WEIGHT, averageClubSize / count)),
      ]),
    );
  }

  return nostalgiaGlobalClubDensityCache[player.club] ?? 1;
}

function nostalgiaDuplicateNameFactor(player: PlayerSeason, modeId: string) {
  if (!isNostalgiaMode(modeId) || !isPre2000NostalgiaCard(player)) {
    return 1;
  }

  if (!nostalgiaHistoricalNameCountsCache) {
    nostalgiaHistoricalNameCountsCache = countBy(players.filter(isPre2000NostalgiaCard).map((candidate) => normalizeName(candidate.name)));
  }

  const sameNameRows = nostalgiaHistoricalNameCountsCache[normalizeName(player.name)] ?? 1;
  if (sameNameRows <= 1) {
    return 1;
  }

  return Math.max(NOSTALGIA_DUPLICATE_NAME_MIN_WEIGHT, 1 / Math.sqrt(sameNameRows));
}

function applyPackEliteExposureBudget(candidates: PlayerSeason[], selected: DraftOffer[]) {
  const capped = candidates.filter((candidate) => isWithinPackEliteExposureBudget(candidate, selected));
  const budgetAlreadySpent =
    selected.filter((offer) => offer.player.score >= ELITE_OFFER_SCORE_FLOOR).length >= MAX_90_PLUS_OFFERS_PER_PACK ||
    selected.filter((offer) => offer.player.score >= HEADLINE_OFFER_SCORE_FLOOR).length >= MAX_94_PLUS_OFFERS_PER_PACK ||
    selected.some((offer) => getIconPeak(offer.player) >= ICON_OFFER_PEAK_FLOOR);

  return budgetAlreadySpent ? capped : capped.length > 0 ? capped : candidates;
}

function isWithinPackEliteExposureBudget(candidate: PlayerSeason, selected: DraftOffer[]) {
  if (candidate.score >= HEADLINE_OFFER_SCORE_FLOOR && selected.filter((offer) => offer.player.score >= HEADLINE_OFFER_SCORE_FLOOR).length >= MAX_94_PLUS_OFFERS_PER_PACK) {
    return false;
  }

  if (candidate.score >= ELITE_OFFER_SCORE_FLOOR && selected.filter((offer) => offer.player.score >= ELITE_OFFER_SCORE_FLOOR).length >= MAX_90_PLUS_OFFERS_PER_PACK) {
    return false;
  }

  if (getIconPeak(candidate) >= ICON_OFFER_PEAK_FLOOR && selected.some((offer) => getIconPeak(offer.player) >= ICON_OFFER_PEAK_FLOOR)) {
    return false;
  }

  return true;
}

function applyPackTeamExposureBudget(candidates: PlayerSeason[], selected: DraftOffer[], modeId?: string) {
  const capped = candidates.filter((candidate) => isWithinPackTeamExposureBudget(candidate, selected, modeId));
  return capped.length > 0 ? capped : candidates;
}

function isWithinPackTeamExposureBudget(candidate: PlayerSeason, selected: DraftOffer[], modeId?: string) {
  const sameClub = selected.filter((offer) => offer.player.club === candidate.club).length;
  const sameClubSeason = selected.filter((offer) => offer.player.club === candidate.club && offer.player.season === candidate.season).length;
  const maxSameClub = modeId === "draft-hard" || isNostalgiaMode(modeId) ? 1 : PACK_MAX_SAME_CLUB;

  return sameClub < maxSameClub && sameClubSeason < PACK_MAX_SAME_CLUB_SEASON;
}

function passesEliteOfferGate(player: PlayerSeason, laneType: string, modeId: string, prompt: DraftPrompt, picks: DraftPick[], packSeed: string) {
  if (player.score < ELITE_OFFER_SCORE_FLOOR && getIconPeak(player) < ICON_OFFER_PEAK_FLOOR) {
    return true;
  }

  const sameSeasonSpine = seasonSpineScore(player, picks) > 0;
  const exactPrompt = player.club === prompt.club && player.era === prompt.era;
  const modernRecent = isModernMode(modeId) && seasonStartYear(player.season) >= 2020;
  const gateRoll = seededNumber(`elite-gate-${packSeed}-${modeId}-${laneType}-${player.id}-${picks.length}`);

  if (modeId === "draft") {
    if (getIconPeak(player) >= ICON_OFFER_PEAK_FLOOR) {
      return laneType === "icon" ? gateRoll < 0.2 : sameSeasonSpine ? gateRoll < 0.09 : exactPrompt ? gateRoll < 0.06 : modernRecent && gateRoll < 0.04;
    }

    if (player.score >= HEADLINE_OFFER_SCORE_FLOOR) {
      return laneType === "icon" ? gateRoll < 0.24 : sameSeasonSpine ? gateRoll < 0.14 : exactPrompt ? gateRoll < 0.09 : modernRecent && gateRoll < 0.06;
    }

    if (laneType === "spine" && sameSeasonSpine) {
      return gateRoll < 0.18;
    }

    if (laneType === "spineStarter") {
      return gateRoll < 0.14;
    }

    return exactPrompt ? gateRoll < 0.1 : modernRecent ? gateRoll < 0.07 : gateRoll < 0.04;
  }

  if (modeId === "draft-hard") {
    if (getIconPeak(player) >= ICON_OFFER_PEAK_FLOOR) {
      return laneType === "icon" ? gateRoll < 0.07 : sameSeasonSpine && gateRoll < 0.025;
    }

    if (player.score >= HEADLINE_OFFER_SCORE_FLOOR) {
      return laneType === "icon" ? gateRoll < 0.11 : sameSeasonSpine ? gateRoll < 0.045 : exactPrompt && gateRoll < 0.024;
    }

    if (laneType === "spine" && sameSeasonSpine) {
      return gateRoll < 0.085;
    }

    if (laneType === "spineStarter") {
      return gateRoll < 0.065;
    }

    return modernRecent ? gateRoll < 0.03 : gateRoll < 0.022;
  }

  if (getIconPeak(player) >= ICON_OFFER_PEAK_FLOOR) {
    return laneType === "icon" ? gateRoll < 0.03 : sameSeasonSpine && gateRoll < 0.01;
  }

  if (player.score >= HEADLINE_OFFER_SCORE_FLOOR) {
    return laneType === "icon" ? gateRoll < 0.07 : sameSeasonSpine ? gateRoll < 0.025 : exactPrompt && gateRoll < 0.014;
  }

  if (laneType === "spine" && sameSeasonSpine) {
    return gateRoll < 0.06;
  }

  if (laneType === "spineStarter") {
    return gateRoll < 0.045;
  }

  return gateRoll < 0.018;
}

function weightedPick(playersToPick: PlayerSeason[], seed: string, weightFor: (player: PlayerSeason) => number) {
  if (playersToPick.length === 0) {
    return undefined;
  }

  const weighted = playersToPick.map((player) => ({ player, weight: weightFor(player) }));
  const total = weighted.reduce((sum, item) => sum + item.weight, 0);
  let cursor = seededNumber(seed) * total;

  for (const item of weighted) {
    cursor -= item.weight;
    if (cursor <= 0) {
      return item.player;
    }
  }

  return weighted[weighted.length - 1].player;
}

function toOffer(player: PlayerSeason, slot: LineupSlot, lane: string, prompt: DraftPrompt, picks: DraftPick[], lang: Language = "it"): DraftOffer {
  const rarity = getRarity(player);
  const chemistry = chemistryScore(player, picks);
  const fit = getCandidateFit(prompt, player, lang);
  const profile = getPlayerProfile(player);
  const leadTag = profile.chemistryTags[0];
  const translatedLane = lane === "Sorpresa" 
    ? translations[lang].packSorpresa 
    : lane;
  const chemLabel = translations[lang].fitChemSuffix;

  return {
    player,
    slot,
    rarity,
    chemistry,
    lane: translatedLane,
    reason: chemistry > 0 ? `${fit}, +${chemistry} ${chemLabel}${leadTag ? ` · ${leadTag}` : ""}` : leadTag ? `${fit} · ${leadTag}` : fit,
    profileTags: profile.chemistryTags.slice(0, 3),
    dataLevel: profile.dataLevel,
  };
}

function getRarity(player: PlayerSeason): DraftRarity {
  const profile = getPlayerProfile(player);
  const peak = getIconPeak(player, profile);

  if (peak >= ICON_RARITY_PEAK_THRESHOLD) {
    return "icona";
  }

  if (peak >= CHAMPION_RARITY_PEAK_THRESHOLD) {
    return "campione";
  }

  if (
    peak >= RARE_RARITY_PEAK_THRESHOLD ||
    (player.source && player.score >= 88) ||
    (profile.dataLevel === "curated-high" && peak >= 89)
  ) {
    return "rara";
  }

  return "comune";
}

function getIconPeak(player: PlayerSeason, profile = getPlayerProfile(player)) {
  return Math.max(player.score, profile.legacy, profile.finishing, profile.creativity, profile.defending);
}

function eliteExposurePenaltyForLane(player: PlayerSeason, rarity: DraftRarity, laneType: string) {
  if (laneType === "icon") {
    return 0;
  }

  if (rarity === "icona") {
    return ICON_NON_LANE_EXPOSURE_PENALTY;
  }

  if (rarity === "campione") {
    return CHAMPION_NON_LANE_EXPOSURE_PENALTY;
  }

  if (rarity === "rara" && getIconPeak(player) >= RARE_RARITY_PEAK_THRESHOLD) {
    return RARE_NON_LANE_EXPOSURE_PENALTY;
  }

  return 0;
}

function chemistryScore(player: PlayerSeason, picks: DraftPick[]) {
  const tagScore = sharedProfileTags(player, picks);
  const seasonSpineBonus = Math.min(4, seasonSpineScore(player, picks) * 2);
  return picks.reduce((score, pick) => {
    const club = pick.player.club === player.club ? 2 : 0;
    const country = pick.player.country === player.country ? 1 : 0;
    const era = pick.player.era === player.era ? 1 : 0;
    return score + club + country + era;
  }, Math.min(3, tagScore) + seasonSpineBonus);
}

function diminishingChemistryBoost(player: PlayerSeason, picks: DraftPick[]) {
  const rawChemistry = chemistryScore(player, picks);
  const earlyValue = Math.min(6, rawChemistry) * 5;
  const lateValue = Math.max(0, rawChemistry - 6) * 1.7;
  return earlyValue + lateValue;
}

function diminishingSeasonSpineBoost(player: PlayerSeason, picks: DraftPick[]) {
  const spine = seasonSpineScore(player, picks);
  return Math.min(2, spine) * 4 + Math.max(0, spine - 2) * 1.2;
}

function seasonSpineScore(player: PlayerSeason, picks: DraftPick[]) {
  return picks.filter((pick) => pick.player.club === player.club && pick.player.season === player.season).length;
}

function sharedProfileTags(player: PlayerSeason, picks: DraftPick[]) {
  const playerTags = new Set(getPlayerProfile(player).chemistryTags);
  return picks.reduce((score, pick) => {
    const shared = getPlayerProfile(pick.player).chemistryTags.filter((tag) => playerTags.has(tag)).length;
    return score + shared;
  }, 0);
}

function profileWeightForLane(profile: ReturnType<typeof getPlayerProfile>, laneType: string) {
  switch (laneType) {
    case "goals":
      return profile.finishing * 0.65 + profile.shooting * 0.35;
    case "starts":
      return profile.workRate * 0.55 + profile.physical * 0.25 + profile.defending * 0.2;
    case "icon":
      return profile.legacy * 0.7 + Math.max(profile.creativity, profile.finishing, profile.defending) * 0.3;
    case "chemistry":
      return profile.workRate * 0.35 + profile.creativity * 0.25 + profile.legacy * 0.4;
    case "spine":
      return profile.workRate * 0.45 + profile.legacy * 0.25 + Math.max(profile.defending, profile.creativity, profile.finishing) * 0.3;
    case "balance":
      return profile.workRate * 0.35 + profile.defending * 0.25 + profile.passing * 0.2 + profile.physical * 0.2;
    case "sacrifice":
      return profile.workRate * 0.55 + profile.physical * 0.25 + Math.max(35, 100 - Math.max(0, profile.legacy - 82)) * 0.2;
    case "spineStarter":
      return profile.legacy * 0.45 + Math.max(profile.creativity, profile.finishing, profile.defending) * 0.35 + profile.workRate * 0.2;
    case "creator":
      return profile.creativity * 0.5 + profile.passing * 0.35 + profile.dribbling * 0.15;
    case "era":
    case "club":
    case "modeClub":
      return profile.legacy * 0.5 + profile.workRate * 0.25 + profile.creativity * 0.25;
    case "gem":
      return profile.workRate * 0.35 + profile.pace * 0.25 + profile.creativity * 0.2 + profile.physical * 0.2;
    default:
      return (
        profile.pace +
        profile.shooting +
        profile.passing +
        profile.dribbling +
        profile.defending +
        profile.physical +
        profile.legacy
      ) / 7;
  }
}

function sourcedSeasonBlockScore(player: PlayerSeason, mode: GameMode) {
  const block = players.filter(
    (candidate) =>
      candidate.club === player.club &&
      candidate.season === player.season &&
      (candidate.evidence?.length || candidate.source) &&
      mode.clubs.includes(candidate.club) &&
      mode.eras.includes(candidate.era),
  );
  const broadRoles = new Set(block.flatMap((candidate) => candidate.roles));
  const uniqueNames = new Set(block.map((candidate) => normalizeName(candidate.name))).size;
  const hasSpineStats = block.filter((candidate) => (candidate.goals ?? 0) > 0 || (candidate.appearances ?? 0) >= 25).length;

  return Math.min(12, uniqueNames) + broadRoles.size * 3 + Math.min(6, hasSpineStats);
}

function modeWeightAdjustment(
  player: PlayerSeason,
  profile: ReturnType<typeof getPlayerProfile>,
  modeId: string,
  laneType: string,
) {
  if (isNostalgiaMode(modeId)) {
    const year2000Drag = modeId === "nostalgia-hard" && player.era === "anni2000" ? -6 : 0;
    const eraBoost = player.era === "anni70" ? 14 : player.era === "anni80" ? 12 : player.era === "anni90" ? 9 : 4;
    const provenanceBoost = player.evidence?.length ? 4 : 0;
    const legendBoost = laneType === "icon" || laneType === "exact" ? Math.max(0, profile.legacy - 90) * 0.5 : 0;
    return eraBoost + provenanceBoost + legendBoost + year2000Drag;
  }

  if (modeId === "draft") {
    const identityBoost = Math.max(0, profile.legacy - 88) * 0.45;
    const seasonStoryBoost = player.evidence?.length ? (laneType === "exact" || laneType === "spine" || laneType === "spineStarter" ? 3 : 1) : 0;
    return identityBoost + seasonStoryBoost + modernRecencyBoost(player, 14, 9, 4);
  }

  if (modeId === "draft-hard") {
    const balanceBoost = isModernHardBalanceCard(player) ? 11 : 0;
    const headlineDrag = isModernHardHeadlineCard(player) ? -10 : 0;
    const workRateBoost = Math.max(0, profile.workRate - 85) * 0.28;
    const seasonStoryBoost = player.evidence?.length ? (laneType === "spine" || laneType === "spineStarter" ? 3 : 1) : 0;
    return balanceBoost + headlineDrag + workRateBoost + seasonStoryBoost + modernRecencyBoost(player, 7, 4, 2);
  }

  if (isProvinciaMode(modeId)) {
    const cultBandBoost = player.score >= 84 && player.score <= 92 ? 12 : player.score < 84 ? 5 : -8;
    const connectorBoost = Math.max(profile.creativity, profile.workRate) >= 90 ? 6 : 0;
    const scorerCreatorBoost = (player.goals ?? 0) >= 10 || (player.assists ?? 0) >= 5 ? 5 : 0;
    return cultBandBoost + connectorBoost + scorerCreatorBoost;
  }

  return 0;
}

function modernRecencyBoost(player: PlayerSeason, current: number, late2010s: number, early2010s: number) {
  const year = seasonStartYear(player.season);
  if (year >= 2020) {
    return current;
  }

  if (year >= 2018) {
    return late2010s;
  }

  if (year >= 2015) {
    return early2010s;
  }

  return 0;
}

function modeShapeWeightAdjustment(player: PlayerSeason, modeId: string, picks: DraftPick[]) {
  if (modeId !== "draft-hard") {
    return 0;
  }

  const pickedClubs = new Set(picks.map((pick) => pick.player.club));
  const missingClubs = Math.max(0, MODERN_HARD_MIN_CLUBS - pickedClubs.size);

  if (missingClubs === 0) {
    return 0;
  }

  const remainingSlotsAfterThisPick = Math.max(0, 11 - picks.length - 1);
  const newClub = !pickedClubs.has(player.club);

  if (newClub) {
    return 12 + missingClubs * 7;
  }

  return missingClubs > remainingSlotsAfterThisPick ? -120 : -missingClubs * 10;
}

function modeDiversityWeightAdjustment(player: PlayerSeason, modeId: string, picks: DraftPick[]) {
  if (modeId !== "draft" && modeId !== "draft-hard") {
    return 0;
  }

  const minClubs = modeId === "draft-hard" ? MODERN_HARD_MIN_CLUBS : MODERN_DRAFT_MIN_CLUBS;
  const clubDragScale = modeId === "draft-hard" ? 12 : 10;
  const clubSeasonDragScale = modeId === "draft-hard" ? 18 : 14;
  const clubCount = picks.filter((pick) => pick.player.club === player.club).length;
  const clubSeasonCount = seasonSpineScore(player, picks);
  const pickedClubs = new Set(picks.map((pick) => pick.player.club));
  const newClubBoost = pickedClubs.size < minClubs && !pickedClubs.has(player.club) ? 18 : 0;
  const clubDrag = clubCount >= 1 ? clubCount * clubDragScale : 0;
  const clubSeasonDrag = clubSeasonCount >= 1 ? clubSeasonCount * clubSeasonDragScale : 0;

  return newClubBoost - clubDrag - clubSeasonDrag;
}

function modeHistoricalVarietyWeightAdjustment(player: PlayerSeason, modeId: string, picks: DraftPick[]) {
  if (!isNostalgiaMode(modeId)) {
    return 0;
  }

  const historicalEras: PlayerSeason["era"][] = ["anni70", "anni80", "anni90"];
  if (!historicalEras.includes(player.era)) {
    return player.era === "anni2000" ? -4 : 0;
  }

  const eraCounts = historicalEras.map((era) => picks.filter((pick) => pick.player.era === era).length);
  const playerEraCount = picks.filter((pick) => pick.player.era === player.era).length;
  const pickedHistoricalCount = eraCounts.reduce((sum, count) => sum + count, 0);
  const minEraCount = Math.min(...eraCounts);
  const sameClubCount = picks.filter((pick) => pick.player.club === player.club).length;
  const sameClubSeasonCount = picks.filter((pick) => pick.player.club === player.club && pick.player.season === player.season).length;
  const underrepresentedEraBoost = playerEraCount <= minEraCount ? 16 : 0;
  const missingEraBoost = pickedHistoricalCount >= 2 && playerEraCount === 0 ? 10 : 0;
  const eraStackDrag = pickedHistoricalCount >= 3 && playerEraCount >= Math.ceil((pickedHistoricalCount + 1) / 2) ? -14 : 0;
  const eightiesDepthBoost = player.era === "anni80" ? 5 : 0;
  const newClubSeasonBoost = sameClubSeasonCount === 0 ? 9 : -sameClubSeasonCount * 18;
  const newClubBoost = sameClubCount === 0 ? 5 : -sameClubCount * 5;

  return underrepresentedEraBoost + missingEraBoost + eraStackDrag + eightiesDepthBoost + newClubSeasonBoost + newClubBoost;
}

function shuffled(list: PlayerSeason[], seed: string) {
  return [...list].sort((left, right) => seededNumber(`${seed}-${left.id}`) - seededNumber(`${seed}-${right.id}`));
}

function shuffledSlots(list: LineupSlot[], seed: string) {
  return [...list].sort((left, right) => seededNumber(`${seed}-${left.id}`) - seededNumber(`${seed}-${right.id}`));
}

function samePlayer(left: PlayerSeason, right: PlayerSeason) {
  return normalizeName(left.name) === normalizeName(right.name);
}

function uniquePlayersByName(list: PlayerSeason[]) {
  const seen = new Set<string>();
  return list.filter((player) => {
    const key = normalizeName(player.name);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function normalizeName(name: string) {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

export function simulateSeason(picks: DraftPick[], lang: Language = "it", mode?: GameMode): SeasonResult {
  const profileScores = picks.map((pick) => playerSeasonImpact(pick));
  const average = profileScores.reduce((total, score) => total + score, 0) / profileScores.length;
  const clubCounts = countBy(picks.map((pick) => pick.player.club));
  const eraCounts = countBy(picks.map((pick) => pick.player.era));
  const defenseScore = lineScore(picks, "DIF");
  const midfieldScore = lineScore(picks, "CEN");
  const attackScore = lineScore(picks, "ATT");
  const workRate = picks.reduce((total, pick) => total + getPlayerProfile(pick.player).workRate, 0) / picks.length;
  const topPlayer = picks
    .map((pick) => ({ pick, impact: playerSeasonImpact(pick) + chemistryScore(pick.player, picks.filter((item) => item !== pick)) * 0.3 }))
    .sort((left, right) => right.impact - left.impact)[0].pick.player.name;

  const chemistry =
    Object.values(clubCounts).filter((count) => count >= 2).length * 1.15 +
    Object.values(eraCounts).filter((count) => count >= 3).length * 0.9;
  const balancePenalty = Math.max(0, Math.max(defenseScore, midfieldScore, attackScore) - Math.min(defenseScore, midfieldScore, attackScore) - 14) / 5;
  const attackBonus = Math.min(4, Math.max(0, attackScore - 84) / 4);
  const controlBonus = Math.min(2.4, Math.max(0, midfieldScore - 85) / 5);
  const defenseBonus = Math.min(2.6, Math.max(0, defenseScore - 85) / 5);
  const workRateBonus = Math.min(1.6, Math.max(0, workRate - 86) / 6);
  const tacticalComplexityBonus = expertDraftTechniqueBonus(picks, defenseScore, midfieldScore, attackScore);
  const pressurePenalty = seasonPressurePenalty(chemistry, workRate, balancePenalty, tacticalComplexityBonus);
  const rating = Math.min(100, average + chemistry + attackBonus + controlBonus + defenseBonus + workRateBonus + tacticalComplexityBonus - balancePenalty - pressurePenalty);
  const outcomeProfile = getSeasonOutcomeProfile(mode?.id);
  const perfectChance = Math.min(outcomeProfile.perfectCap, perfectSeasonChance({
    rating,
    chemistry,
    defenseScore,
    midfieldScore,
    attackScore,
    workRate,
    balancePenalty,
    pressurePenalty,
    techniqueBonus: tacticalComplexityBonus,
  }) * outcomeProfile.perfectMultiplier);
  const perfectRoll = seededNumber(`perfect-season-${picks.map((pick) => `${pick.slot.id}:${pick.player.id}`).sort().join("|")}`);
  const isPerfectSeason = perfectChance > 0 && perfectRoll < perfectChance;
  const weakestLine = Math.min(defenseScore, midfieldScore, attackScore);
  const isEliteNearMiss =
    !isPerfectSeason &&
    rating >= outcomeProfile.eliteNearMiss.rating &&
    chemistry >= outcomeProfile.eliteNearMiss.chemistry &&
    weakestLine >= outcomeProfile.eliteNearMiss.weakestLine &&
    tacticalComplexityBonus >= outcomeProfile.eliteNearMiss.techniqueBonus &&
    pressurePenalty <= outcomeProfile.eliteNearMiss.pressurePenalty;

  const isUnbeatenByQuality =
    rating >= outcomeProfile.unbeaten.rating &&
    chemistry >= outcomeProfile.unbeaten.chemistry &&
    weakestLine >= outcomeProfile.unbeaten.weakestLine &&
    tacticalComplexityBonus >= outcomeProfile.unbeaten.techniqueBonus &&
    pressurePenalty <= outcomeProfile.unbeaten.pressurePenalty;

  const losses = isPerfectSeason || isEliteNearMiss || isUnbeatenByQuality ? 0 : rating >= 99.15 ? 1 : rating >= 96.4 ? 2 : rating >= 92.6 ? 3 : rating >= 88 ? 5 : 7;
  const draws = isPerfectSeason ? 0 : isEliteNearMiss ? 2 : isUnbeatenByQuality ? (rating >= 99.7 ? 4 : 6) : rating >= 99.15 ? 5 : rating >= 96.4 ? 7 : rating >= 92.6 ? 9 : 12;
  const wins = 38 - draws - losses;
  const points = wins * 3 + draws;

  if (wins === 38) {
    return {
      wins,
      draws,
      losses,
      points,
      rating: Math.round(rating),
      chemistry: roundOne(chemistry),
      attackBonus: roundOne(attackBonus),
      topPlayer,
      title: translations[lang].simOut38winsTitle,
      detail: translations[lang].simOut38winsDetail,
    };
  }

  if (losses === 0) {
    return {
      wins,
      draws,
      losses,
      points,
      rating: Math.round(rating),
      chemistry: roundOne(chemistry),
      attackBonus: roundOne(attackBonus),
      topPlayer,
      title: translations[lang].simOutNoLossesTitle,
      detail: translations[lang].simOutNoLossesDetail,
    };
  }

  if (points >= 96) {
    return {
      wins,
      draws,
      losses,
      points,
      rating: Math.round(rating),
      chemistry: roundOne(chemistry),
      attackBonus: roundOne(attackBonus),
      topPlayer,
      title: translations[lang].simOutScudettoTitle,
      detail: translations[lang].simOutScudettoDetail,
    };
  }

  return {
    wins,
    draws,
    losses,
    points,
    rating: Math.round(rating),
    chemistry: roundOne(chemistry),
    attackBonus: roundOne(attackBonus),
    topPlayer,
    title: translations[lang].simOutChampionsTitle,
    detail: translations[lang].simOutChampionsDetail,
  };
}


interface PerfectSeasonInputs {
  rating: number;
  chemistry: number;
  defenseScore: number;
  midfieldScore: number;
  attackScore: number;
  workRate: number;
  balancePenalty: number;
  pressurePenalty: number;
  techniqueBonus: number;
}

function seasonPressurePenalty(chemistry: number, workRate: number, balancePenalty: number, techniqueBonus: number) {
  const chemistryDrag = Math.max(0, 4 - chemistry) * 0.22;
  const workRateDrag = Math.max(0, 87 - workRate) * 0.09;
  const techniqueRelief = Math.min(0.28, techniqueBonus * 0.08);

  return Math.max(0.45, SERIE_A_PRESSURE_TAX + chemistryDrag + workRateDrag + balancePenalty * 0.22 - techniqueRelief);
}

function getSeasonOutcomeProfile(modeId?: string) {
  const defaultProfile = {
    perfectMultiplier: 1,
    perfectCap: PERFECT_SEASON_CHANCE_CAP,
    eliteNearMiss: {
      rating: 99.65,
      chemistry: 5.9,
      weakestLine: 92.0,
      techniqueBonus: 2.2,
      pressurePenalty: 1.28,
    },
    unbeaten: {
      rating: 99.45,
      chemistry: 5.4,
      weakestLine: 91.2,
      techniqueBonus: 2.0,
      pressurePenalty: 1.32,
    },
  };

  if (modeId === "draft") {
    return {
      perfectMultiplier: 2.2,
      perfectCap: 0.028,
      eliteNearMiss: {
        rating: 99.25,
        chemistry: 4.8,
        weakestLine: 90.0,
        techniqueBonus: 1.45,
        pressurePenalty: 1.58,
      },
      unbeaten: {
        rating: 98.6,
        chemistry: 4.2,
        weakestLine: 88.8,
        techniqueBonus: 1.0,
        pressurePenalty: 1.7,
      },
    };
  }

  if (modeId === "draft-hard") {
    return {
      perfectMultiplier: 1.25,
      perfectCap: 0.016,
      eliteNearMiss: {
        rating: 99.55,
        chemistry: 5.4,
        weakestLine: 91.4,
        techniqueBonus: 2.0,
        pressurePenalty: 1.42,
      },
      unbeaten: {
        rating: 99.15,
        chemistry: 4.9,
        weakestLine: 90.2,
        techniqueBonus: 1.55,
        pressurePenalty: 1.55,
      },
    };
  }

  return defaultProfile;
}

function perfectSeasonChance({
  rating,
  chemistry,
  defenseScore,
  midfieldScore,
  attackScore,
  workRate,
  balancePenalty,
  pressurePenalty,
  techniqueBonus,
}: PerfectSeasonInputs) {
  const weakestLine = Math.min(defenseScore, midfieldScore, attackScore);

  if (
    rating < 99.15 ||
    weakestLine < 90.4 ||
    chemistry < 5.0 ||
    workRate < 88.4 ||
    balancePenalty > 0.35 ||
    pressurePenalty > 1.32 ||
    techniqueBonus < 1.75
  ) {
    return 0;
  }

  const ratingLift = Math.min(0.011, Math.max(0, rating - ELITE_RATING_FLOOR) * 0.005);
  const lineLift = Math.min(0.008, Math.max(0, weakestLine - 89) * 0.0022);
  const chemistryLift = Math.min(0.007, Math.max(0, chemistry - 4.6) * 0.002);
  const techniqueLift = Math.min(0.008, Math.max(0, techniqueBonus - 1.35) * 0.005);

  return Math.min(PERFECT_SEASON_CHANCE_CAP, 0.0025 + ratingLift + lineLift + chemistryLift + techniqueLift);
}

function expertDraftTechniqueBonus(picks: DraftPick[], defenseScore: number, midfieldScore: number, attackScore: number) {
  const clubSeasonCounts = countBy(picks.map((pick) => `${pick.player.club}-${pick.player.season}`));
  const compactSpines = Object.values(clubSeasonCounts).filter((count) => count >= 2).length;
  const roleCoverageBySpine = Object.entries(clubSeasonCounts).reduce((covered, [spineKey, count]) => {
    if (count < 2) {
      return covered;
    }

    const spineRoles = new Set(
      picks
        .filter((pick) => `${pick.player.club}-${pick.player.season}` === spineKey)
        .map((pick) => pick.slot.role),
    );

    return covered + (spineRoles.size >= 2 ? 1 : 0);
  }, 0);
  const eraBands = new Set(picks.map((pick) => pick.player.era)).size;
  const clubs = new Set(picks.map((pick) => pick.player.club)).size;
  const lineSpread = Math.max(defenseScore, midfieldScore, attackScore) - Math.min(defenseScore, midfieldScore, attackScore);
  const balanceBonus = lineSpread <= 8 ? 0.8 : lineSpread <= 12 ? 0.45 : 0;
  const spineBonus = Math.min(1.4, compactSpines * 0.45 + roleCoverageBySpine * 0.25);
  const varietyBonus = eraBands >= 3 && clubs >= 3 ? 0.35 : 0;

  return spineBonus + balanceBonus + varietyBonus;
}

function uniquePlayers(list: PlayerSeason[]) {
  return Array.from(new Map(list.map((player) => [player.id, player])).values());
}

void uniquePlayers;

function playerSeasonImpact(pick: DraftPick) {
  const profile = getPlayerProfile(pick.player);
  const roleScore =
    pick.slot.role === "POR"
      ? profile.defending * 0.58 + profile.workRate * 0.22 + profile.passing * 0.1 + profile.physical * 0.1
      : pick.slot.role === "DIF"
        ? profile.defending * 0.52 + profile.physical * 0.2 + profile.passing * 0.13 + profile.workRate * 0.15
        : pick.slot.role === "CEN"
          ? profile.passing * 0.34 + profile.creativity * 0.26 + profile.workRate * 0.18 + profile.dribbling * 0.12 + profile.defending * 0.1
          : profile.finishing * 0.35 + profile.shooting * 0.22 + profile.dribbling * 0.18 + profile.pace * 0.15 + profile.creativity * 0.1;

  return roleScore * 0.82 + pick.player.score * 0.18;
}

function lineScore(picks: DraftPick[], role: LineupSlot["role"]) {
  const line = picks.filter((pick) => pick.slot.role === role);
  if (line.length === 0) {
    return 0;
  }

  return line.reduce((total, pick) => total + playerSeasonImpact(pick), 0) / line.length;
}

function countBy(values: string[]) {
  return values.reduce<Record<string, number>>((counts, value) => {
    counts[value] = (counts[value] ?? 0) + 1;
    return counts;
  }, {});
}

function roundOne(value: number) {
  return Math.round(value * 10) / 10;
}

function seededNumber(seed: string) {
  let hash = 2166136261;

  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0) / 4294967295;
}
